import { useCallback } from 'react';
import { useMutation } from 'react-query';
import { useSnackbar } from 'notistack';

import {
  suspendMenu,
  VariablesSuspend,
  unsuspendMenu,
  VariablesUnsuspend,
  updateMenu,
  VariablesUpdate,
  ResponseStatus,
  exportDraftMenu,
  VariablesExport,
  VariablesImageUpload,
  ResponseImageUpload,
  uploadIDraftImage,
} from 'data/requests/menuActions';
import { updateMenuStatus, updateCachedItem } from 'data/cacheHelpers/menu';
import { CDError } from 'utils/error';
import { saveFile } from 'utils/file';

const useMenuActions = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: suspendMenuMutation, isLoading: suspendLoading } =
    useMutation<ResponseStatus, CDError, VariablesSuspend>(suspendMenu);

  const { mutateAsync: unsuspendMenuMutation, isLoading: unsuspendLoading } =
    useMutation<ResponseStatus, CDError, VariablesUnsuspend>(unsuspendMenu);

  const { mutateAsync: updateMenuMutation, isLoading: updateLoading } =
    useMutation<ResponseStatus, CDError, VariablesUpdate>(updateMenu);

  const { mutateAsync: exportMenuMutation, isLoading: exportLoading } =
    useMutation<Blob, CDError, VariablesExport>(exportDraftMenu);

  const { mutateAsync: uploadImageMutation, isLoading: uploadImageLoading } =
    useMutation<ResponseImageUpload, CDError, VariablesImageUpload>(
      uploadIDraftImage,
    );

  const suspend = useCallback(
    async ({ brandId, itemId, pauseType }: VariablesSuspend) => {
      try {
        const response = await suspendMenuMutation({
          brandId,
          itemId,
          pauseType,
        });
        updateCachedItem({
          brandId,
          itemId,
          dataToUpdate: { isUpdatingStatus: true, status: pauseType },
        });

        return response;
      } catch (error) {
        enqueueSnackbar(`Status change failed`, {
          variant: 'error',
        });
        return error;
      }
    },
    [suspendMenuMutation, enqueueSnackbar],
  );

  const unsuspend = useCallback(
    async ({ brandId, itemId }: VariablesUnsuspend) => {
      try {
        const response = await unsuspendMenuMutation({
          brandId,
          itemId,
        });
        updateCachedItem({
          brandId,
          itemId,
          dataToUpdate: { isUpdatingStatus: true, status: 'ACTIVE' },
        });
        return response;
      } catch (error) {
        enqueueSnackbar(`Status change failed`, {
          variant: 'error',
        });
        return error;
      }
    },

    [unsuspendMenuMutation, enqueueSnackbar],
  );

  const update = useCallback(
    async ({ brandId, menu }: VariablesUpdate) => {
      try {
        const response = await updateMenuMutation({
          brandId,
          menu,
        });
        updateMenuStatus(brandId, 'PROCESSING');
        return response;
      } catch (error) {
        return error;
      }
    },

    [updateMenuMutation],
  );

  const exportDraft = useCallback(
    async ({ brandId, usePrintNames }: VariablesExport) => {
      try {
        const response = await exportMenuMutation({ brandId, usePrintNames });
        saveFile(`menu-${brandId}.xlsx`, response);
        return response;
      } catch (error) {
        enqueueSnackbar(`Export error: ${error}`, {
          variant: 'error',
        });
        return error;
      }
    },

    [enqueueSnackbar, exportMenuMutation],
  );

  const uploadImageCallback = useCallback(
    async (params: VariablesImageUpload) => {
      try {
        const response = await uploadImageMutation(params);
        return response;
      } catch (error) {
        enqueueSnackbar('Failed to upload image', {
          variant: 'error',
        });
        return error as CDError;
      }
    },
    [enqueueSnackbar, uploadImageMutation],
  );

  return {
    isLoading: suspendLoading || unsuspendLoading || updateLoading,
    suspend,
    unsuspend,
    update,
    exportDraft,
    exportLoading,
    uploadImage: uploadImageCallback,
    uploadImageLoading,
  };
};

export default useMenuActions;
